import { gql } from '@apollo/client/core';
import { reactive, computed, toRefs } from '@vue/composition-api';
import { executeQuery } from '@/components/commons/utils.js';
import Tracking from '@/components/commons/Tracking.js';

const REVIEWS_QUERY = gql`
query reviews($product: ID!, $page: Int, $rating: String, $tag: String) {
  reviews(product: $product, page: $page, rating: $rating, tag: $tag) {
    reviews {
      comments
      city
      design
      featured
      firstName
      images {
        imageUrl
        thumbnailUrl
      }
      lastName
      id
      productHashKey
      activeVersionHashKey
      alternativeHashKeys
      productImageUrl
      productSlug
      productTitle
      quality
      rating
      regionCode
      regionName
      reviewDate
      tags
      value
      response
    }
    maxPage
    page
    count
    countExactProduct
    availableTags {
      name
      count
    }
  }
}`;

export default function useProductReviews() {
  const state = reactive({
    filters: {
      product: null, // product hash key
      tag: '',
      rating: ''
    },
    availableTags: [],
    count: 0,
    countExactProduct: 0,
    page: 0,
    maxPage: 0,
    reviews: [],
    loading: false,
    error: false,
    isMore: computed(() => state.page < state.maxPage - 1)
  });

  async function load(product, { tag = '', rating = '' } = {}) {
    if(!product) {
      console.warn('No product provided to load reviews for');
      return state;
    }

    state.filters.product = product;
    state.filters.tag = tag;
    state.filters.rating = rating;
    state.count = 0;
    state.countExactProduct = 0;
    state.page = -1;
    state.maxPage = 0;
    state.reviews = [];
    state.error = false;
    state.loading = false;

    const result = await loadMore();
    return result;
  }

  async function loadMore({ replace = false } = {}) {
    if(state.loading) {
      console.warn('Load more called while reviews already loading');
      return state;
    }

    state.loading = true;
    const page = state.page;
    const product = state.filters.product;

    try {
      const result = await executeQuery(REVIEWS_QUERY, {
        product: state.filters.product,
        page: state.page + 1,
        rating: state.filters.rating,
        tag: state.filters.tag
      });

      if(result && result.reviews) {
        if(state.filters.product !== product || state.page !== page) {
          // discard stale result
          return state;
        }

        if(result.reviews.countExactProduct && result.reviews.availableTags) {
          const TAGGED_REVIEWS_DISPLAY_PERCENT = 80;
          const TAGGED_REVIEWS_MIN_COUNT = 20;
          const taggedCount = result.reviews.availableTags.reduce((acc, current) => current.count + acc, 0); // We don't have a good way of counting tagged vs non-tagged reviews, so just use total tag count.
          const taggedPercent = (taggedCount / result.reviews.countExactProduct) * 100;

          if(taggedCount >= TAGGED_REVIEWS_MIN_COUNT || taggedPercent >= TAGGED_REVIEWS_DISPLAY_PERCENT) {
            state.availableTags = result.reviews.availableTags;
          } else {
            state.availableTags = [];
          }
        } else {
          state.availableTags = [];
        }

        state.reviews = replace ? result.reviews.reviews : [...(state.reviews || []), ...(result.reviews.reviews || [])];
        state.count = result.reviews.count;
        state.countExactProduct = result.reviews.countExactProduct;
        state.page = result.reviews.page;
        state.maxPage = result.reviews.maxPage;
      } else {
        state.error = true;
      }
    } catch(e) {
      state.error = true;
      Tracking.sentryCaptureError(e);
    }

    state.loading = false;

    return state;
  }

  async function updateTagsFilter(tag) {
    state.filters.tag = tag;
    state.page = -1;
    await loadMore({ replace: true });
  }

  async function updateStarsFilter(rating) {
    state.filters.rating = rating;
    state.page = -1;
    await loadMore({ replace: true });
  }

  async function resetFilter() {
    await load(state.filters.product);
  }

  return {
    ...toRefs(state),
    load,
    loadMore,
    updateTagsFilter,
    updateStarsFilter,
    resetFilter
  };
}
